import React, { useRef, useState } from 'react';
import { toast } from 'sonner';
import { Upload, Loader2, ImageIcon, X, Link2 } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { useLang } from '@/lib/i18n';
import { errMsg } from '@/lib/api';
import { uploadFile, ACCEPT_IMAGES, ACCEPT_ALL } from '@/lib/upload';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';

/** Generic upload button. onUploaded(attachment) per file. */
export const FileUploadButton = ({ kind = 'document', refId, multiple = false, imagesOnly = false, onUploaded, children, variant = 'outline', size = 'sm', className = '', testid = 'file-upload-button', disabled }) => {
  const { company } = useAuth();
  const { lang } = useLang();
  const ref = useRef(null);
  const [progress, setProgress] = useState(null);
  const pick = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = '';
    for (const f of files) {
      setProgress(0);
      try {
        const att = await uploadFile(company.id, f, { kind, refId }, setProgress);
        onUploaded?.(att);
      } catch (err) { toast.error(errMsg(err, err.message)); }
    }
    setProgress(null);
  };
  return (
    <>
      <input ref={ref} type="file" className="hidden" multiple={multiple} accept={imagesOnly ? ACCEPT_IMAGES : ACCEPT_ALL} onChange={pick} data-testid={`${testid}-input`} />
      <Button type="button" variant={variant} size={size} className={`rounded-xl ${className}`} onClick={() => ref.current?.click()} disabled={disabled || progress !== null} data-testid={testid}>
        {progress !== null ? <><Loader2 size={14} className="mr-1.5 animate-spin" />{progress}%</> : (children || <><Upload size={14} className="mr-1.5" />{lang === 'id' ? 'Unggah' : 'Upload'}</>)}
      </Button>
    </>
  );
};

/** Image field with preview, upload, URL fallback and clear. value/onChange are URL strings. */
export const ImageUploadField = ({ value, onChange, kind = 'website', refId, label, shape = 'square', disabled, testid = 'image-upload', allowUrl = true, size = 'md' }) => {
  const { lang } = useLang();
  const [showUrl, setShowUrl] = useState(false);
  const id = lang === 'id';
  const dims = size === 'lg' ? 'h-28 w-28' : size === 'wide' ? 'h-28 w-full aspect-video' : 'h-20 w-20';
  const radius = shape === 'circle' ? 'rounded-full' : 'rounded-2xl';
  return (
    <div className="space-y-2" data-testid={testid}>
      {label && <div className="text-xs text-muted-foreground">{label}</div>}
      <div className="flex items-start gap-3">
        <div className={`${dims} ${radius} shrink-0 overflow-hidden border border-dashed border-border bg-muted/50 flex items-center justify-center`}>
          {value ? <img src={value} alt="" className="h-full w-full object-cover" data-testid={`${testid}-preview`} /> : <ImageIcon className="text-muted-foreground" size={22} />}
        </div>
        <div className="flex flex-col gap-1.5 min-w-0 flex-1">
          <div className="flex flex-wrap gap-1.5">
            <FileUploadButton kind={kind} refId={refId} imagesOnly disabled={disabled} testid={`${testid}-button`} onUploaded={(att) => onChange(att.url)} />
            {allowUrl && <Button type="button" variant="ghost" size="sm" className="rounded-xl text-muted-foreground" disabled={disabled} onClick={() => setShowUrl((v) => !v)} data-testid={`${testid}-url-toggle`}><Link2 size={14} className="mr-1" />URL</Button>}
            {value && <Button type="button" variant="ghost" size="sm" className="rounded-xl text-muted-foreground hover:text-destructive" disabled={disabled} onClick={() => onChange('')} data-testid={`${testid}-clear`}><X size={14} className="mr-1" />{id ? 'Hapus' : 'Remove'}</Button>}
          </div>
          {showUrl && <Input value={value || ''} onChange={(e) => onChange(e.target.value)} placeholder="https://..." className="rounded-xl text-sm h-8" disabled={disabled} data-testid={`${testid}-url-input`} />}
          <p className="text-[11px] text-muted-foreground">{id ? 'JPG, PNG, GIF, WebP · maks 20 MB' : 'JPG, PNG, GIF, WebP · max 20 MB'}</p>
        </div>
      </div>
    </div>
  );
};
